import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/utils';

const badgeVariants = cva(
  cn(
    'inline-flex items-center rounded-md px-2.5 py-1 text-sm font-normal transition-colors',
    'focus:ring-ring focus:ring-2 focus:ring-offset-2 focus:outline-hidden',
    'whitespace-nowrap',
  ),
  {
    variants: {
      variant: {
        primary: 'bg-brand-400 text-white dark:text-brand-100 dark:bg-brand-300',
        secondary: 'bg-steel-50 text-steel-400 dark:text-steel-200 dark:bg-brand-400',
        tertiary: 'border border-peach-500 bg-transparent text-fire-500',
        success: 'bg-wtw-success-25 text-wtw-success-450 font-bold',
        danger: 'bg-wtw-coralreef-50 text-wtw-coralreef-500 font-bold',
        'danger-outline': 'border border-fire-500 bg-fire-2 text-fire-500',
        'danger-inverted': 'bg-fire-500 text-white dark:bg-[#341A29] dark:text-fire-400',
        info: 'bg-wtw-stratosphere-25 text-wtw-stratosphere-600 font-bold',
        'info-outline': 'border border-brand-400 bg-brand-100 text-brand-400',
        neutral: 'bg-wtw-graymatter-50 text-wtw-graymatter-500 font-bold',
        upcoming: 'bg-fire-100 text-peach-600',
        warning: 'bg-wtw-warning-25 text-wtw-mandarin-500 font-bold',
        glitter: 'bg-glitter-100 text-steel-500',
      },
      smallCaps: {
        true: 'text-xs font-semibold uppercase tracking-wide',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
);

export type BadgeVariants = VariantProps<typeof badgeVariants>;
export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, BadgeVariants {}

// Maps legacy v1 badge types to semantic v2 variants so any stale strings from
// backends or un-migrated callers always resolve to a valid semantic style.
const VARIANT_ALIAS_MAP: Partial<Record<string, BadgeVariants['variant']>> = {
  primary: 'info',
  secondary: 'neutral',
  tertiary: 'danger',
  'danger-outline': 'danger',
  'danger-inverted': 'danger',
  'info-outline': 'info',
  upcoming: 'warning',
  glitter: 'neutral',
};

function Badge({ className, variant, smallCaps = false, ...props }: BadgeProps) {
  const resolvedVariant = (variant && VARIANT_ALIAS_MAP[variant as string]) ?? variant;
  return <div className={cn(badgeVariants({ variant: resolvedVariant, smallCaps }), className)} {...props} />;
}

export { Badge, badgeVariants };
